import { useRef, useEffect, useState } from 'react';
import { ShippingLocation } from '@/types';
import * as countryStateData from '../../../country-state.js';

interface CountryStateSelectorProps {
  locations: ShippingLocation[];
  selectedCountry: string;
  selectedState: string;
  countrySearch: string;
  stateSearch: string;
  showCountryDropdown: boolean;
  showStateDropdown: boolean;
  onCountrySearchChange: (value: string) => void;
  onStateSearchChange: (value: string) => void;
  onCountrySelect: (code: string) => void;
  onStateSelect: (state: string) => void;
  onToggleCountryDropdown: () => void;
  onToggleStateDropdown: () => void;
}

export function CountryStateSelector({
  locations,
  selectedCountry,
  selectedState,
  countrySearch,
  stateSearch,
  showCountryDropdown,
  showStateDropdown,
  onCountrySearchChange,
  onStateSearchChange,
  onCountrySelect,
  onStateSelect,
  onToggleCountryDropdown,
  onToggleStateDropdown,
}: CountryStateSelectorProps) {
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const stateDropdownRef = useRef<HTMLDivElement>(null);
  const [countries, setCountries] = useState<string[]>([]);
  const [states, setStates] = useState<string[]>([]);

  useEffect(() => {
    const uniqueCountries = [...new Set(locations
      .filter(loc => loc.country && loc.country !== '0')
      .map(loc => loc.country))];
    setCountries(uniqueCountries);
  }, [locations]);

  useEffect(() => {
    if (selectedCountry) {
      const uniqueStates = [...new Set(
        locations
          .filter(loc => loc.country === selectedCountry)
          .map(loc => loc.state)
      )];
      setStates(uniqueStates);
    } else {
      setStates([]);
    }
  }, [selectedCountry, locations]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
        if (showCountryDropdown) {
          onToggleCountryDropdown();
        }
      }
      if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target as Node)) {
        if (showStateDropdown) {
          onToggleStateDropdown();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onToggleCountryDropdown, onToggleStateDropdown, showCountryDropdown, showStateDropdown]);

  const filteredStates = states.filter(state => 
    state.toLowerCase().includes((stateSearch || '').toLowerCase())
  );

  const filteredCountries = countries.filter(countryCode =>
    countryStateData.countries.find(c => c.code === countryCode)?.name.toLowerCase().includes((countrySearch || '').toLowerCase()) ||
    countryCode.toLowerCase().includes((countrySearch || '').toLowerCase())
  );

  const getCountryName = (code: string) => {
    return countryStateData.countries.find(c => c.code === code)?.name || code;
  };

  const getStateName = (countryCode: string, stateCode: string) => {
    const countryStates = countryStateData.states[countryCode];
    if (countryStates) {
      const state = countryStates.find(s => s.code === stateCode);
      return state ? state.name : stateCode;
    }
    return stateCode;
  };

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="relative country-dropdown" ref={countryDropdownRef}>
        <label className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-2">
          Country
        </label>
        <div className="relative">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (showStateDropdown) {
                onToggleStateDropdown();
              }
              onToggleCountryDropdown();
            }}
            className="w-full h-12 px-4 text-left rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {selectedCountry ? getCountryName(selectedCountry) : 'Select Country'}
          </button>
          {showCountryDropdown && (
            <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
              <div className="p-2">
                <input
                  type="text"
                  placeholder="Search country..."
                  value={countrySearch}
                  onChange={(e) => onCountrySearchChange(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="max-h-48 overflow-auto">
                {filteredCountries.map((countryCode) => (
                  <button
                    key={countryCode}
                    onClick={(e) => {
                      e.stopPropagation();
                      onCountrySelect(countryCode);
                      onToggleCountryDropdown();
                    }}
                    className={`w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 ${
                      selectedCountry === countryCode
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                        : 'text-gray-900 dark:text-white'
                    }`}
                  >
                    {getCountryName(countryCode)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedCountry && (
        <div className="relative state-dropdown" ref={stateDropdownRef}>
          <label className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-2">
            State
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (showCountryDropdown) {
                  onToggleCountryDropdown();
                }
                onToggleStateDropdown();
              }}
              className="w-full h-12 px-4 text-left rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {selectedState ? getStateName(selectedCountry, selectedState) : 'Select State'}
            </button>
            {showStateDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
                <div className="p-2">
                  <input
                    type="text"
                    placeholder="Search state..."
                    value={stateSearch}
                    onChange={(e) => onStateSearchChange(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="max-h-48 overflow-auto">
                  {filteredStates.length > 0 ? (
                    filteredStates.map((stateCode) => (
                      <button
                        key={stateCode}
                        onClick={(e) => {
                          e.stopPropagation();
                          onStateSelect(stateCode);
                          onToggleStateDropdown();
                        }}
                        className={`w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 ${
                          selectedState === stateCode
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                            : 'text-gray-900 dark:text-white'
                        }`}
                      >
                        {getStateName(selectedCountry, stateCode)}
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-center text-gray-500 dark:text-gray-400 text-sm">
                      No states found
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}